import { Injectable, Inject } from '@angular/core';
import { createClient, ContentfulClientApi, AssetCollection } from 'contentful';
import { Observable, from, zip, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { map, switchMap, tap, catchError, take } from 'rxjs/operators';

import { AssetMapperService } from './asset.mapper.service';
import { AssetMapped, AssetsCollection } from './_interfaces_/asset.mapped.interface';
import { selectAirlineAssets } from '@store/media-assets/media-assets.selectors';
import * as AssetActions from '@store/media-assets/media-assets.actions';
import { AppState } from '@store/state';
import { ConfigService } from '../config/config.service';

const MEDIA_ASSETS_TOKEN = 'eacb2223ba66a8b58c7cde984ed4872520b54acc607ec14b9fde70b577e20b31';

@Injectable()
export class AssetService {
  public arlineLogosAssetsClient: ContentfulClientApi;

  constructor(
    @Inject(AssetMapperService) private assetMapperService: AssetMapperService,
    @Inject(Store) private store: Store<AppState>,
    @Inject(ConfigService) private configService: ConfigService
    ) {
    this.arlineLogosAssetsClient = createClient({
      space: 'lwkcf2qb54sx',
      accessToken: MEDIA_ASSETS_TOKEN,
    });
  }

  public getAirlineAssets(assetKeys: string[]): Observable<AssetsCollection> {
    let storedAssets: AssetsCollection = {};
    return this.store.select(selectAirlineAssets).pipe(
      take(1),
      tap((assets) => (storedAssets = assets)),
      switchMap((assets: AssetsCollection) => this.getFilteredAssets(assets, assetKeys)),
      map((newAssets) => this.assetMapperService.concatAssets(newAssets, storedAssets)),
      tap((assets) => this.store.dispatch(AssetActions.setAirlineAssets({ airlines: assets }))),
      catchError(() => of(storedAssets))
    );
  }

  public getFilteredAssets(assets: AssetsCollection, assetKeys: string[]): Observable<AssetMapped[] | null> {
    const assetCalls: Observable<AssetMapped>[] = assetKeys.filter((code) => !assets[code]).map((code) => this.getMappedAirlineAsset(code));
    if (assetCalls.length === 0) {
      return of(null);
    }
    return zip(...assetCalls);
  }

  public getAirlineAsset(assetKey: string): Observable<AssetCollection> {
    return from(
      this.arlineLogosAssetsClient.getAssets({
        select: 'fields',
        'fields.title': `AL_${assetKey}_sq`,
      })
    );
  }

  public getMappedAirlineAsset(assetKey: string): Observable<AssetMapped> {
    const s3airlineLogosUrl = this.configService.getProperty<string>('S3_AIRLINE_LOGOS_URL');
    const airlineLogo: AssetMapped = {
            id: '',
            title: `AL_${assetKey}_sq`,
            url: `${s3airlineLogosUrl}/AL_${assetKey}_sq.png`
          };
    return of(airlineLogo);
  }
}
